import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from '../../../../components/Intl/withIntl.js';
import { Table } from 'react-bootstrap';

import HtmlComponent from '../../../../components/Text/HtmlComponent.js';

// Nutrition rows in EU declaration order. `sub` = indented "of which ..." line.
const NUTRITION_ROWS = [
    { key: 'fat', label: 'Fat', unit: 'g' },
    { key: 'saturatedFat', label: 'of which saturates', unit: 'g', sub: true },
    { key: 'carbohydrates', label: 'Carbohydrate', unit: 'g' },
    { key: 'sugars', label: 'of which sugars', unit: 'g', sub: true },
    { key: 'protein', label: 'Protein', unit: 'g' },
    { key: 'salt', label: 'Salt', unit: 'g' },
];

const capitalize = value => `${value.charAt(0).toUpperCase()}${value.slice(1)}`;

function formatNumber(value, locale) {
    if(value === null || value === undefined || value === '') return null;
    const number = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if(Number.isNaN(number)) return null;
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(number);
}

// True when the structured nutrition object holds at least one usable value.
export function hasNutritionValues(nutrition) {
    return !!nutrition && (nutrition.energyKcal || nutrition.energyKj || NUTRITION_ROWS.some(row => nutrition[row.key] !== undefined && nutrition[row.key] !== null));
}

function ProductNutrition({ product, fallback = null, intl }) {
    const nutrition = (product.foodData || {}).nutrition;

    // No structured data: keep the free CMS text (if any) so nothing is lost.
    if(!hasNutritionValues(nutrition)) {
        return fallback ? <HtmlComponent text={fallback} /> : null;
    }

    return (
        <Table size="sm" borderless className="mb-1 bg-transparent product-nutrition">
            <thead>
                <tr className="border-bottom">
                    <th className="fw-normal" aria-label="nutrient" />
                    <th className="text-end text-nowrap"><FormattedMessage id="productNutritionPer100g" defaultMessage="Per 100 g" /></th>
                </tr>
            </thead>
            <tbody>
                {(nutrition.energyKj || nutrition.energyKcal) && (
                    <tr className="border-bottom">
                        <td><FormattedMessage id="productNutritionEnergy" defaultMessage="Energy" /></td>
                        <td className="text-end text-nowrap">
                            {[
                                formatNumber(nutrition.energyKj, intl.locale) && `${formatNumber(nutrition.energyKj, intl.locale)} kJ`,
                                formatNumber(nutrition.energyKcal, intl.locale) && `${formatNumber(nutrition.energyKcal, intl.locale)} kcal`,
                            ].filter(Boolean).join(' / ')}
                        </td>
                    </tr>
                )}
                {NUTRITION_ROWS.map(row => {
                    const value = formatNumber(nutrition[row.key], intl.locale);
                    if(value === null) return null;
                    return (
                        <tr key={row.key} className="border-bottom">
                            <td className={row.sub ? 'ps-3 text-black-50' : ''}>
                                <FormattedMessage id={`productNutrition${capitalize(row.key)}`} defaultMessage={row.label} />
                            </td>
                            <td className="text-end text-nowrap">{value} {row.unit}</td>
                        </tr>
                    );
                })}
            </tbody>
        </Table>
    );
}

ProductNutrition.propTypes = {
    product: PropTypes.object.isRequired,
    fallback: PropTypes.string,
    intl: PropTypes.object.isRequired,
};

export default injectIntl(ProductNutrition);
